import Button from "@edaz/edazkit/Button/index";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import { Input } from "@frui.ts/bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import BaseAccountNumberInput from "controls/finance/baseAccountNumberInput";
import CzechAccountInput from "controls/finance/czechAccountInput";
import IbanInput from "controls/finance/ibanInput";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import { observer } from "mobx-react-lite";
import React from "react";
import { ButtonToolbar, Col, Form } from "react-bootstrap";
import { formatAmount, translateEnum, translateGeneral } from "utils/localizations";
import type PayGateToTransferRefundDetailViewModel from "viewModels/finance/refundApprovals/payGateToTransferRefunds/payGateToTransferRefundDetailViewModel";
import type TransferRefundDetailViewModel from "viewModels/finance/refundApprovals/transferRefunds/transferRefundDetailViewModel";
import BankCodeInputGroup from "views/common/bankCodeInputGroup";
import CountrySelect from "views/common/countrySelect";
import PossibleSEPAPaymentWarning from "views/common/possibleSEPAPaymentWarning";
import Section from "views/common/section";
import TransferPaymentType from "entities/transferPaymentType";
import FeeType from "entities/feeType";

const RefundPaymentSectionView: ViewComponent<TransferRefundDetailViewModel | PayGateToTransferRefundDetailViewModel> = observer(
  ({ vm }) => {
    const refundPayment = vm.item.refundPayment;
    let buttons;

    if (vm.isPaymentEditable) {
      buttons = (
        <ButtonToolbar className="ml-auto">
          {vm.hasUnsavedChanges && (
            <Button size="sm" className="rounded text-light" variant="link" onClick={vm.onCancelTransferRefundModificationClick}>
              {translateGeneral("cancelUnsavedChanges")}
            </Button>
          )}
          <Button size="sm" className="rounded" disabled={!vm.hasUnsavedChanges} onClick={vm.onSaveTransferRefundPaymentClick}>
            {translateGeneral("saveChanges")}
          </Button>
        </ButtonToolbar>
      );
    }

    return (
      <Section title={vm.translate("refund.title")} context={vm.item} buttons={buttons}>
        {refundPayment.transferType !== TransferPaymentType.Domestic ? (
          <>
            <Form.Row>
              <FormGroup as={Col} md={6} target={refundPayment} property="transferType">
                <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "transferType")}</FormLabel>
                <AdvancedSelect custom items={vm.refundTypeOptions} mode="key" disabled={!vm.isPaymentTypeEditable} />
              </FormGroup>
              {refundPayment.transferType === TransferPaymentType.Foreign && (
                <FormGroup as={Col} md={6} target={refundPayment.counterpartyBank} property="codeType">
                  <FormLabel>{vm.translateModelAttribute("refundCounterpartyBankRequest", "codeType")}</FormLabel>
                  <AdvancedSelect allowEmpty items={vm.bankCodeOptions} disabled={!vm.isPaymentEditable} />
                </FormGroup>
              )}
            </Form.Row>
            <Form.Row>
              {refundPayment.transferType === TransferPaymentType.Sepa ? (
                <FormGroup as={Col} md={6} target={refundPayment.counterpartyBank} property="accountNumber">
                  <FormLabel>{translateGeneral("finance.iban")}</FormLabel>
                  <IbanInput disabled={!vm.isPaymentEditable} onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
                </FormGroup>
              ) : (
                <FormGroup as={Col} md={6} target={refundPayment.counterpartyBank} property="accountNumber">
                  <FormLabel>{vm.translateModelAttribute("refundCounterpartyBankRequest", "accountNumber")}</FormLabel>
                  <BaseAccountNumberInput disabled={!vm.isPaymentEditable} />
                </FormGroup>
              )}
              <BankCodeInputGroup
                target={refundPayment.counterpartyBank}
                codeType={refundPayment.counterpartyBank.codeType}
                disabled={!vm.isPaymentEditable}
              />
            </Form.Row>
            <hr className="mt-0" />
          </>
        ) : (
          <Form.Row>
            <FormGroup as={Col} lg={12} xl={4} target={refundPayment} property="transferType">
              <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "transferType")}</FormLabel>
              <AdvancedSelect custom items={vm.refundTypeOptions} mode="key" disabled={!vm.isPaymentEditable} />
            </FormGroup>
            <FormGroup as={Col} lg={12} xl={8} target={refundPayment} property="counterpartyBank">
              <FormLabel required={true}>
                {vm.translateModelAttribute("refundCounterpartyBankRequest", "accountNumber")}
              </FormLabel>
              <CzechAccountInput disabled={!vm.isPaymentEditable} onInvalidValuePasted={vm.onInvalidAccountNumberPasted} />
            </FormGroup>
          </Form.Row>
        )}
        {refundPayment.transferType === TransferPaymentType.Foreign && (
          <>
            <Form.Row>
              <FormGroup as={Col} md={7} target={refundPayment.counterpartyBank} property="name">
                <FormLabel>{vm.translateModelAttribute("refundCounterpartyBankRequest", "name")}</FormLabel>
                <Input disabled={!vm.isPaymentEditable} />
              </FormGroup>
            </Form.Row>
            <Form.Row>
              <FormGroup as={Col} md={12} target={refundPayment.counterpartyBank} property="cityAddress">
                <FormLabel>{vm.translateModelAttribute("refundCounterpartyBankRequest", "cityAddress")}</FormLabel>
                <Input disabled={!vm.isPaymentEditable} />
              </FormGroup>
              <FormGroup as={Col} md={6} target={refundPayment.counterpartyBank} property="countryCode">
                <FormLabel>{vm.translateModelAttribute("refundCounterpartyBankRequest", "countryCode")}</FormLabel>
                <CountrySelect vm={vm.countrySelectViewModel} allowEmpty disabled={!vm.isPaymentEditable} />
              </FormGroup>
              <PossibleSEPAPaymentWarning show={vm.showSEPAWarning} />
            </Form.Row>
            <hr className="mt-0" />
          </>
        )}
        {(refundPayment.transferType === TransferPaymentType.Sepa ||
          refundPayment.transferType === TransferPaymentType.Foreign) && (
          <>
            <Form.Row>
              <FormGroup as={Col} md={6} target={refundPayment} property="counterpartyName">
                <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "counterpartyName")}</FormLabel>
                <Input disabled={!vm.isPaymentEditable} />
              </FormGroup>
              <FormGroup as={Col} md={6} target={refundPayment} property="counterpartyCountryCode">
                <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "counterpartyCountryCode")}</FormLabel>
                <CountrySelect
                  vm={vm.countrySelectViewModel}
                  allowEmpty
                  disabled={!vm.isPaymentEditable}
                  onlySEPACountries={refundPayment.transferType === TransferPaymentType.Sepa}
                />
              </FormGroup>
            </Form.Row>
            <Form.Row>
              <FormGroup as={Col} md={6} target={refundPayment} property="counterpartyStreetAddress">
                <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "counterpartyStreetAddress")}</FormLabel>
                <Input disabled={!vm.isPaymentEditable} />
              </FormGroup>
              <FormGroup as={Col} md={6} target={refundPayment} property="counterpartyCityAddress">
                <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "counterpartyCityAddress")}</FormLabel>
                <Input disabled={!vm.isPaymentEditable} />
              </FormGroup>
            </Form.Row>
            <hr className="mt-0" />
          </>
        )}
        <Form.Row>
          <Form.Group as={Col} xl={6}>
            <Form.Label>{translateEnum("feeType", FeeType.ChargeRefundFee)}</Form.Label>
            <FakeInput target={vm} property="chargeRefundFee" valueFormatter={formatAmount} />
          </Form.Group>
          <Form.Group as={Col} xl={6}>
            <Form.Label>{vm.translate("bankFees")}</Form.Label>
            <FakeInput target={vm} property="bankFees" valueFormatter={formatAmount} />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} xl={6}>
            <Form.Label>{vm.translateModelAttribute("refund", "refundAmount")}</Form.Label>
            <FakeInput target={vm.item} property="refundAmount" valueFormatter={(amount: number) => formatAmount(amount)} />
          </Form.Group>
          <Form.Group as={Col} xl={6}>
            <Form.Label>{vm.translateModelAttribute("refund", "officialRecordId")}</Form.Label>
            <FakeInput target={vm.item} property="officialRecordId" />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col}>
            <Form.Label>{vm.translateModelAttribute("refund", "refundPaymentTemplateId")}</Form.Label>
            <FakeInput
              target={vm.item}
              property="refundPaymentTemplateId"
              valueFormatter={(refundPaymentTemplateId: string) => vm.translateRefundReason(refundPaymentTemplateId)}
            />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <FormGroup as={Col} target={refundPayment} property="additionalAvMessage">
            <FormLabel>{vm.translateModelAttribute("transferRefundPayment", "additionalAvMessage")}</FormLabel>
            <Input disabled={!vm.isPaymentEditable} />
          </FormGroup>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>{vm.translateModelAttribute("merchantBankAccount", "accountNumber")}</Form.Label>
            <FakeInput
              target={vm.item.refundSenderBankAccount}
              property="accountNumber"
              valueFormatter={() =>
                formatBankAccount({
                  accountNumber: `${vm.item.refundSenderBankAccount.accountNumber}/${vm.item.refundSenderBankAccount.bankCode}`,
                })
              }
            />
          </Form.Group>
        </Form.Row>
      </Section>
    );
  }
);

export default RefundPaymentSectionView;
